import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { FilterState, ResourceType, Severity } from '@shared/types';
import type { Environment } from '@shared/types';
import { RESOURCE_CATEGORIES } from '@shared/constants';
import { serialize as serializeFilterState, deserialize as deserializeFilterState } from '@shared/filter-serializer';
import { getFilterOptions } from '../lib/api-client';
import type { FilterOptions } from '../lib/api-client';

/* ─── Filter State Context ─────────────────────────────────────────── */

type FilterStateTuple = [FilterState, (filters: FilterState) => void];

const FilterStateContext = createContext<FilterStateTuple | null>(null);

/**
 * Hook to access and update the shared filter state.
 * Must be used within a component tree that includes FilterBar, or
 * it provides a standalone implementation backed by URL search params.
 */
export function useFilterState(): FilterStateTuple {
  const ctx = useContext(FilterStateContext);
  if (ctx) return ctx;

  // Standalone fallback using search params directly
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [searchParams, setSearchParams] = useSearchParams();
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [filters, setFiltersInternal] = useState<FilterState>(() =>
    deserializeFilterState(searchParams)
  );

  const setFilters = (newFilters: FilterState) => {
    setFiltersInternal(newFilters);
    const params = serializeFilterState(newFilters);
    setSearchParams(params, { replace: true });
  };

  return [filters, setFilters];
}

/* ─── FilterBar Props ──────────────────────────────────────────────── */

export interface FilterBarProps {
  onFilterChange?: (filters: FilterState) => void;
  onClearAll?: () => void;
  showSeverity?: boolean;
  showSearch?: boolean;
}

/* ─── Constants ────────────────────────────────────────────────────── */

const PROVIDER_OPTIONS: { value: 'gcp' | 'aws'; label: string }[] = [
  { value: 'gcp', label: 'GCP' },
  { value: 'aws', label: 'AWS' },
];

const SEVERITY_OPTIONS: { value: Severity; label: string }[] = [
  { value: 'critical', label: 'Critical' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

const ENVIRONMENT_OPTIONS: { value: Environment; label: string }[] = [
  { value: 'dev', label: 'Dev' },
  { value: 'qa', label: 'QA' },
  { value: 'prd', label: 'Prd' },
  { value: 'other', label: 'Other' },
];

const ALL_RESOURCE_TYPES: { value: ResourceType; label: string }[] = Object.entries(
  RESOURCE_CATEGORIES
).flatMap(([, types]) =>
  types.map((t) => ({
    value: t,
    label: t
      .split('_')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' '),
  }))
);

function getEmptyFilterState(): FilterState {
  return {
    providers: [],
    resourceTypes: [],
    regions: [],
    projectsOrAccounts: [],
    environments: [],
    severities: [],
    search: '',
  };
}

function filtersAreEmpty(filters: FilterState): boolean {
  return (
    filters.providers.length === 0 &&
    filters.resourceTypes.length === 0 &&
    filters.regions.length === 0 &&
    filters.projectsOrAccounts.length === 0 &&
    filters.environments.length === 0 &&
    filters.severities.length === 0 &&
    filters.search === ''
  );
}

/**
 * Ensures currently selected values always appear in dropdown options even if
 * the cascading API no longer returns them. This prevents orphaned selections
 * the user can't deselect.
 */
function mergeSelectedIntoOptions<T extends string>(
  apiOptions: { value: T; label: string }[],
  selectedOptions: { value: T; label: string }[]
): { value: T; label: string }[] {
  const existingValues = new Set(apiOptions.map((o) => o.value));
  const extras = selectedOptions.filter((s) => !existingValues.has(s.value));
  return [...apiOptions, ...extras];
}

/* ─── FilterBar Component ──────────────────────────────────────────── */

export function FilterBar({
  onFilterChange,
  onClearAll,
  showSeverity = false,
  showSearch = true,
}: FilterBarProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState<FilterState>(() =>
    deserializeFilterState(searchParams)
  );
  const isInitialMount = useRef(true);
  const [dynamicOptions, setDynamicOptions] = useState<FilterOptions>({
    regions: [],
    resourceTypes: [],
    projectsOrAccounts: [],
    providers: [],
    environments: [],
  });

  // Reload filter options from API whenever filter selections change (cascading filters).
  // Each dimension is filtered by the OTHER active dimensions.
  useEffect(() => {
    const params: Record<string, string | undefined> = {};
    if (filters.providers.length > 0) {
      params.provider = filters.providers.join(',');
    }
    if (filters.resourceTypes.length > 0) {
      params.type = filters.resourceTypes.join(',');
    }
    if (filters.regions.length > 0) {
      params.region = filters.regions.join(',');
    }
    if (filters.projectsOrAccounts.length > 0) {
      params.project = filters.projectsOrAccounts.join(',');
    }
    if (filters.environments.length > 0) {
      params.environment = filters.environments.join(',');
    }

    getFilterOptions(params)
      .then((res) => {
        if (res.data) setDynamicOptions(res.data);
      })
      .catch(() => {});
  }, [filters.providers, filters.resourceTypes, filters.regions, filters.projectsOrAccounts, filters.environments]);

  // Restore filters from URL on initial load and notify parent
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      const restored = deserializeFilterState(searchParams);
      setFilters(restored);
      onFilterChange?.(restored);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync state → URL and notify parent whenever filters change
  const updateFilters = useCallback(
    (newFilters: FilterState) => {
      setFilters(newFilters);
      const params = serializeFilterState(newFilters);
      setSearchParams(params, { replace: true });
      onFilterChange?.(newFilters);
    },
    [setSearchParams, onFilterChange]
  );

  const handleProviderToggle = useCallback(
    (provider: 'gcp' | 'aws') => {
      setFilters((prev) => {
        const next = { ...prev };
        if (next.providers.includes(provider)) {
          next.providers = next.providers.filter((p) => p !== provider);
        } else {
          next.providers = [...next.providers, provider];
        }
        updateFilters(next);
        return next;
      });
    },
    [updateFilters]
  );

  const handleResourceTypeToggle = useCallback(
    (type: ResourceType) => {
      setFilters((prev) => {
        const next = { ...prev };
        if (next.resourceTypes.includes(type)) {
          next.resourceTypes = next.resourceTypes.filter((t) => t !== type);
        } else {
          next.resourceTypes = [...next.resourceTypes, type];
        }
        updateFilters(next);
        return next;
      });
    },
    [updateFilters]
  );

  const handleSeverityToggle = useCallback(
    (severity: Severity) => {
      setFilters((prev) => {
        const next = { ...prev };
        if (next.severities.includes(severity)) {
          next.severities = next.severities.filter((s) => s !== severity);
        } else {
          next.severities = [...next.severities, severity];
        }
        updateFilters(next);
        return next;
      });
    },
    [updateFilters]
  );

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setFilters((prev) => {
        const next = { ...prev, search: value };
        // Only propagate filter if search is empty or has 2+ chars
        if (value.length === 0 || value.length >= 2) {
          updateFilters(next);
        }
        return next;
      });
    },
    [updateFilters]
  );

  const handleClearAll = useCallback(() => {
    const empty = getEmptyFilterState();
    setFilters(empty);
    setSearchParams(new URLSearchParams(), { replace: true });
    onClearAll?.();
    onFilterChange?.(empty);
  }, [setSearchParams, onClearAll, onFilterChange]);

  const setFilterState = useCallback(
    (newFilters: FilterState) => {
      updateFilters(newFilters);
    },
    [updateFilters]
  );

  const hasActiveFilters = !filtersAreEmpty(filters);

  return (
    <FilterStateContext.Provider value={[filters, setFilterState]}>
      <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-center gap-3">
          {/* Search input */}
          {showSearch && (
            <div className="relative min-w-[200px] flex-1">
              <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search resources (2+ chars)..."
                value={filters.search}
                onChange={handleSearchChange}
                className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                aria-label="Search resources"
              />
            </div>
          )}

          {/* Provider multi-select dropdown */}
          <MultiSelectDropdown
            label="Provider"
            options={PROVIDER_OPTIONS}
            selected={filters.providers}
            onToggle={handleProviderToggle}
          />

          {/* Environment multi-select dropdown */}
          <MultiSelectDropdown
            label="Environment"
            options={ENVIRONMENT_OPTIONS}
            selected={filters.environments}
            onToggle={(env) => {
              const next = { ...filters };
              if (next.environments.includes(env)) {
                next.environments = next.environments.filter((v) => v !== env);
              } else {
                next.environments = [...next.environments, env];
              }
              updateFilters(next);
            }}
          />

          {/* Resource Type multi-select dropdown (loaded from API) */}
          <MultiSelectDropdown
            label="Resource Type"
            options={mergeSelectedIntoOptions(
              dynamicOptions.resourceTypes.map((t) => ({
                value: t,
                label: t.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
              })),
              filters.resourceTypes.map((t) => ({
                value: t,
                label: t.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
              }))
            )}
            selected={filters.resourceTypes}
            onToggle={handleResourceTypeToggle}
          />

          {/* Region multi-select dropdown (loaded from API) */}
          <MultiSelectDropdown
            label="Region"
            options={mergeSelectedIntoOptions(
              [{ value: '__global__', label: 'Global' }, ...dynamicOptions.regions.map((r) => ({ value: r, label: r }))],
              filters.regions.map((r) => ({ value: r, label: r === '__global__' ? 'Global' : r }))
            )}
            selected={filters.regions}
            onToggle={(region) => {
              const next = { ...filters };
              if (next.regions.includes(region)) {
                next.regions = next.regions.filter((v) => v !== region);
              } else {
                next.regions = [...next.regions, region];
              }
              updateFilters(next);
            }}
          />

          {/* Project/Account multi-select dropdown (loaded from API) */}
          <MultiSelectDropdown
            label="Project/Account"
            options={mergeSelectedIntoOptions(
              dynamicOptions.projectsOrAccounts.map((p) => ({ value: p, label: p })),
              filters.projectsOrAccounts.map((p) => ({ value: p, label: p }))
            )}
            selected={filters.projectsOrAccounts}
            onToggle={(project) => {
              const next = { ...filters };
              if (next.projectsOrAccounts.includes(project)) {
                next.projectsOrAccounts = next.projectsOrAccounts.filter((v) => v !== project);
              } else {
                next.projectsOrAccounts = [...next.projectsOrAccounts, project];
              }
              updateFilters(next);
            }}
          />

          {/* Severity filter (conditionally shown) */}
          {showSeverity && (
            <MultiSelectDropdown
              label="Severity"
              options={SEVERITY_OPTIONS}
              selected={filters.severities}
              onToggle={handleSeverityToggle}
            />
          )}

          {/* Clear all button */}
          {hasActiveFilters && (
            <button
              onClick={handleClearAll}
              className="flex items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
              aria-label="Clear all filters"
            >
              <XIcon className="h-4 w-4" />
              Clear all
            </button>
          )}
        </div>

        {/* Active filter badges */}
        {hasActiveFilters && (
          <div className="mt-3 flex flex-wrap gap-2">
            {filters.providers.map((p) => (
              <FilterBadge
                key={`provider-${p}`}
                label={p.toUpperCase()}
                onRemove={() => handleProviderToggle(p)}
              />
            ))}
            {filters.resourceTypes.map((t) => (
              <FilterBadge
                key={`type-${t}`}
                label={t.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                onRemove={() => handleResourceTypeToggle(t)}
              />
            ))}
            {filters.regions.map((r) => (
              <FilterBadge
                key={`region-${r}`}
                label={r === '__global__' ? 'Global' : r}
                onRemove={() => {
                  const next = { ...filters, regions: filters.regions.filter((v) => v !== r) };
                  updateFilters(next);
                }}
              />
            ))}
            {filters.projectsOrAccounts.map((p) => (
              <FilterBadge
                key={`project-${p}`}
                label={p}
                onRemove={() => {
                  const next = {
                    ...filters,
                    projectsOrAccounts: filters.projectsOrAccounts.filter((v) => v !== p),
                  };
                  updateFilters(next);
                }}
              />
            ))}
            {filters.environments.map((env) => (
              <FilterBadge
                key={`env-${env}`}
                label={ENVIRONMENT_OPTIONS.find(o => o.value === env)?.label || env}
                onRemove={() => {
                  const next = {
                    ...filters,
                    environments: filters.environments.filter((v) => v !== env),
                  };
                  updateFilters(next);
                }}
              />
            ))}
            {filters.severities.map((s) => (
              <FilterBadge
                key={`severity-${s}`}
                label={s.charAt(0).toUpperCase() + s.slice(1)}
                onRemove={() => handleSeverityToggle(s)}
              />
            ))}
            {filters.search.length >= 2 && (
              <FilterBadge
                label={`Search: "${filters.search}"`}
                onRemove={() => {
                  const next = { ...filters, search: '' };
                  updateFilters(next);
                }}
              />
            )}
          </div>
        )}
      </div>
    </FilterStateContext.Provider>
  );
}

/* ─── Sub-components ───────────────────────────────────────────────── */

interface MultiSelectDropdownProps<T extends string> {
  label: string;
  options: { value: T; label: string }[];
  selected: T[];
  onToggle: (value: T) => void;
}

function MultiSelectDropdown<T extends string>({
  label,
  options,
  selected,
  onToggle,
}: MultiSelectDropdownProps<T>) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1 rounded-md border px-3 py-2 text-sm font-medium transition-colors ${
          selected.length > 0
            ? 'border-primary-300 bg-primary-50 text-primary-700'
            : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
        }`}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={`Filter by ${label}`}
      >
        {label}
        {selected.length > 0 && (
          <span className="ml-1 rounded-full bg-primary-600 px-1.5 py-0.5 text-xs text-white">
            {selected.length}
          </span>
        )}
        <ChevronDownIcon className="ml-1 h-4 w-4" />
      </button>
      {open && (
        <div
          className="absolute left-0 top-full z-50 mt-1 max-h-60 w-56 overflow-y-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg"
          role="listbox"
          aria-label={`${label} options`}
        >
          {options.map((opt) => {
            const isSelected = selected.includes(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                role="option"
                aria-selected={isSelected}
                onClick={() => onToggle(opt.value)}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-100"
              >
                <span
                  className={`flex h-4 w-4 items-center justify-center rounded border ${
                    isSelected
                      ? 'border-primary-600 bg-primary-600 text-white'
                      : 'border-gray-300'
                  }`}
                >
                  {isSelected && <CheckIcon className="h-3 w-3" />}
                </span>
                {opt.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

interface TagInputProps {
  label: string;
  values: string[];
  onChange: (values: string[]) => void;
}

function TagInput({ label, values, onChange }: TagInputProps) {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault();
      if (!values.includes(inputValue.trim())) {
        onChange([...values, inputValue.trim()]);
      }
      setInputValue('');
    }
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1 rounded-md border px-3 py-2 text-sm font-medium transition-colors ${
          values.length > 0
            ? 'border-primary-300 bg-primary-50 text-primary-700'
            : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
        }`}
        aria-expanded={open}
        aria-label={`Filter by ${label}`}
      >
        {label}
        {values.length > 0 && (
          <span className="ml-1 rounded-full bg-primary-600 px-1.5 py-0.5 text-xs text-white">
            {values.length}
          </span>
        )}
        <ChevronDownIcon className="ml-1 h-4 w-4" />
      </button>
      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 w-56 rounded-md border border-gray-200 bg-white p-3 shadow-lg">
          <input
            type="text"
            placeholder={`Add ${label.toLowerCase()}...`}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            aria-label={`Add ${label.toLowerCase()}`}
          />
          {values.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {values.map((v) => (
                <span
                  key={v}
                  className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700"
                >
                  {v}
                  <button
                    type="button"
                    onClick={() => onChange(values.filter((x) => x !== v))}
                    className="text-gray-500 hover:text-gray-700"
                    aria-label={`Remove ${v}`}
                  >
                    <XIcon className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FilterBadge({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-primary-50 px-2.5 py-1 text-xs font-medium text-primary-700">
      {label}
      <button
        type="button"
        onClick={onRemove}
        className="text-primary-500 hover:text-primary-700"
        aria-label={`Remove filter ${label}`}
      >
        <XIcon className="h-3 w-3" />
      </button>
    </span>
  );
}

/* ─── Icons ────────────────────────────────────────────────────────── */

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
    </svg>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
    </svg>
  );
}

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
    </svg>
  );
}
